import React, { useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers, faHandshake, faCommentDollar, faBarcode,
  faBoxes, faKey, faDollarSign, faFile, faStore,
  faReceipt, faRectangleAd,
} from '@fortawesome/free-solid-svg-icons'
import Header from './Header'
import { Link } from 'react-router-dom'
import jwt_decode from 'jwt-decode'
import AuthContext from '../../middlewares/AuthContext'
import packageJson from '../../../package.json'
import { useTranslation } from 'react-i18next'

// ── Types ─────────────────────────────────────────────────────────────────────
interface AuthCtx {
  token: string | null
}

interface Props {
  showNavbar: (toggleId: string, navId: string, bodyId: string, headerId: string) => void
  flip: boolean
}

// ── NavItem helper ─────────────────────────────────────────────────────────────
const NavItem: React.FC<{ to: string; icon: any; label: string }> = ({ to, icon, label }) => (
  <Link to={to} className="nav_link">
    <FontAwesomeIcon icon={icon} fixedWidth style={{ fontSize: '1.15rem', flexShrink: 0, opacity: 0.9 }} />
    <span className="nav_name">{label}</span>
  </Link>
)

// ── SideBar ───────────────────────────────────────────────────────────────────
const SideBar: React.FC<Props> = ({ showNavbar, flip }) => {
  const { token }        = useContext(AuthContext) as AuthCtx
  const { t }            = useTranslation()
  const tokenDecode: any = token !== null ? jwt_decode(token) : null

  const hasRole = (name: string) =>
    tokenDecode?.roles?.some((r: any) => r.name === name)

  return (
    <>
      <Header showNavbar={showNavbar} flip={flip} />

      <div className="l-navbar shadow-lg" id="nav-bar">
        <nav className="nav">

          {/* Top — logo + links */}
          <div>
            <Link className="nav_logo" to="/dashboard">
              <img
                src="https://labeldictate.com/assets/img/Label-Dictate-Logo-V2021.png"
                width={80}
                alt="Label Dictate"
              />
            </Link>

            <div className="nav_list">

              {/* Admin */}
              {hasRole('admin') && (
                <>
                  <NavItem to="/users"          icon={faUsers}          label={t('sidebar.users')} />
                  <NavItem to="/stores"         icon={faStore}          label={t('sidebar.stores')} />
                  <NavItem to="/partners"       icon={faHandshake}      label={t('sidebar.partners')} />
                  <NavItem to="/marketings"     icon={faCommentDollar}  label={t('sidebar.marketing')} />
                  <NavItem to="/products-plans" icon={faBarcode}        label={t('sidebar.products_plans')} />
                  <NavItem to="/payments"       icon={faDollarSign}     label={t('sidebar.payments')} />
                </>
              )}

              {/* Customer */}
              {hasRole('customer') && (
                <NavItem to="/payments/customer" icon={faFile} label={t('sidebar.billing')} />
              )}

              {/* Partner */}
              {hasRole('partner') && (
                <NavItem to="/payments/partner" icon={faFile} label={t('sidebar.billing')} />
              )}

              {/* Marketing */}
              {hasRole('marketing') && (
                <>
                  <NavItem to="/payments/marketing"        icon={faFile}         label={t('sidebar.billing')} />
                  <NavItem to="/marketing/advertising/create" icon={faRectangleAd} label={t('sidebar.add_advertising')} />
                </>
              )}

              {/* Partner / Admin / Subpartner */}
              {(hasRole('partner') || hasRole('admin') || hasRole('subpartner')) && (
                <>
                  <NavItem to="/reports"  icon={faReceipt} label={t('sidebar.reports')} />
                  <NavItem to="/packages" icon={faBoxes}   label={t('sidebar.packages')} />
                </>
              )}

              {/* Partner / Subpartner / Admin / Customer */}
              {(hasRole('partner') || hasRole('subpartner') || hasRole('admin') || hasRole('customer')) && (
                <NavItem to="/licenses" icon={faKey} label={t('sidebar.licenses')} />
              )}

            </div>
          </div>

          {/* Bottom — version */}
          <div>
            <div
              className="nav_link"
              style={{ cursor: 'default', opacity: 0.45, fontSize: '0.75rem', pointerEvents: 'none' }}
            >
              <span style={{ width: '1em', textAlign: 'center', flexShrink: 0, fontSize: '0.7rem' }}>v</span>
              <span className="nav_name">{packageJson.version}</span>
            </div>
          </div>

        </nav>
      </div>
    </>
  )
}

export default SideBar
